<template>
    <Dialog :open="open" @update:open="handleClose">
        <DialogContent class="sm:max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader>
                <DialogTitle>Copy Installation Code</DialogTitle>
                <DialogDescription>
                    Check our detailed
                    <a
                        href="https://docs.topol.io/plugin"
                        target="_blank"
                        class="text-new-blue-500 hover:underline"
                        >documentation on Plugin</a
                    >.
                </DialogDescription>
            </DialogHeader>

            <!-- Tabs: HTML vs JS/TS Package -->
            <Tabs v-model="tab" class="flex-1 flex flex-col min-h-0">
                <TabsList class="grid w-full grid-cols-2 shrink-0">
                    <TabsTrigger value="html">HTML</TabsTrigger>
                    <TabsTrigger value="package">JS/TS Package</TabsTrigger>
                </TabsList>

                <!-- HTML Tab -->
                <TabsContent value="html" class="flex-1 overflow-y-auto space-y-4 pr-1 mt-4">
                    <p class="text-sm text-muted-foreground">
                        First, insert this HTML within your application:
                    </p>

                    <div class="relative rounded-md overflow-hidden">
                        <Button
                            variant="secondary"
                            size="sm"
                            class="absolute right-2 top-2 z-10"
                            @click="copy(htmlDivCode, 'htmlDiv')"
                        >
                            {{ copied.htmlDiv ? 'Copied!' : 'Copy' }}
                        </Button>
                        <div v-if="htmlDivHighlighted" class="text-sm [&>pre]:!rounded-none [&>pre]:!m-0 [&>pre]:p-4 [&>pre]:!whitespace-pre-wrap [&>pre]:break-all" v-html="htmlDivHighlighted"></div>
                        <CodeSkeleton v-else :widths="htmlDivSkeleton" />
                    </div>

                    <p class="text-xs text-muted-foreground">
                        This is where Plugin will be displayed.<br />
                        Be sure to set explicit height and width, or the div may be
                        too small for the Email editor.
                    </p>

                    <div class="relative rounded-md overflow-hidden">
                        <Button
                            variant="secondary"
                            size="sm"
                            class="absolute right-2 top-2 z-10"
                            @click="copy(htmlScriptCode, 'htmlScript')"
                        >
                            {{ copied.htmlScript ? 'Copied!' : 'Copy' }}
                        </Button>
                        <div v-if="htmlScriptHighlighted" class="text-sm [&>pre]:!rounded-none [&>pre]:!m-0 [&>pre]:p-4 [&>pre]:!whitespace-pre-wrap [&>pre]:break-all" v-html="htmlScriptHighlighted"></div>
                        <CodeSkeleton v-else :widths="htmlScriptSkeleton" />
                    </div>
                </TabsContent>

                <!-- JS/TS Package Tab -->
                <TabsContent value="package" class="flex-1 overflow-y-auto space-y-4 pr-1 mt-4">
                    <div>
                        <p class="text-sm text-new-neutral-700 font-medium mb-2">
                            Install Editor from NPM or Yarn using:
                        </p>
                        <div class="relative rounded-md overflow-hidden">
                            <Button
                                variant="secondary"
                                size="sm"
                                class="absolute right-2 top-2 z-10"
                                @click="copy('npm install @topol.io/editor', 'install')"
                            >
                                {{ copied.install ? 'Copied!' : 'Copy' }}
                            </Button>
                            <div v-if="installHighlighted" class="text-sm [&>pre]:!rounded-none [&>pre]:!m-0 [&>pre]:p-4 [&>pre]:!whitespace-pre-wrap [&>pre]:break-all" v-html="installHighlighted"></div>
                            <CodeSkeleton v-else :widths="installSkeleton" />
                        </div>
                    </div>

                    <div>
                        <p class="text-sm text-new-neutral-700 font-medium mb-1">
                            Add HTML element Editor will load to:
                        </p>
                        <div class="relative rounded-md overflow-hidden">
                            <Button
                                variant="secondary"
                                size="sm"
                                class="absolute right-2 top-2 z-10"
                                @click="copy(htmlDivCode, 'pkgHtml')"
                            >
                                {{ copied.pkgHtml ? 'Copied!' : 'Copy' }}
                            </Button>
                            <div v-if="htmlDivHighlighted" class="text-sm [&>pre]:!rounded-none [&>pre]:!m-0 [&>pre]:p-4 [&>pre]:!whitespace-pre-wrap [&>pre]:break-all" v-html="htmlDivHighlighted"></div>
                            <CodeSkeleton v-else :widths="htmlDivSkeleton" />
                        </div>
                        <p class="text-xs text-muted-foreground mt-1">
                            Be sure to set explicit height and width, or the div may be
                            too small for the Email editor.
                        </p>
                    </div>

                    <div>
                        <p class="text-sm text-new-neutral-700 font-medium mb-1">
                            In your JS or TS file import TopolPlugin and initialize the editor:
                        </p>
                        <p class="text-xs text-muted-foreground mb-2">
                            For more options see the
                            <a
                                href="https://docs.topol.io/plugin"
                                target="_blank"
                                class="text-new-blue-500 hover:underline"
                            >docs for TopolOptions configuration</a>.
                        </p>
                        <div class="relative rounded-md overflow-hidden">
                            <Button
                                variant="secondary"
                                size="sm"
                                class="absolute right-2 top-2 z-10"
                                @click="copy(packageScriptCode, 'pkgScript')"
                            >
                                {{ copied.pkgScript ? 'Copied!' : 'Copy' }}
                            </Button>
                            <div v-if="pkgScriptHighlighted" class="text-sm [&>pre]:!rounded-none [&>pre]:!m-0 [&>pre]:p-4 [&>pre]:!whitespace-pre-wrap [&>pre]:break-all" v-html="pkgScriptHighlighted"></div>
                            <CodeSkeleton v-else :widths="pkgScriptSkeleton" />
                        </div>
                    </div>

                    <p class="text-xs text-muted-foreground">
                        You can find full documentation on Editor package
                        <a
                            href="https://docs.topol.io/plugin"
                            target="_blank"
                            class="text-new-blue-500 hover:underline"
                        >here</a>.
                    </p>
                </TabsContent>
            </Tabs>

            <DialogFooter class="shrink-0 mt-4">
                <Button variant="outline" @click="handleClose">Close</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, reactive, watch, computed, h, PropType } from 'vue';
import { useClipboard } from '@vueuse/core';
import { codeToHtml } from 'shiki';
import IToken from '@/types/Plugin/IToken';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/components/ui/tabs';

// Inline placeholder rendered while shiki finishes highlighting. Mimics the
// dark `github-dark` block (background, padding, line height) so the dialog
// doesn't jump around when the highlighted output replaces it.
const CodeSkeleton = (props: { widths: string[] }) =>
    h(
        'div',
        { class: 'bg-[#24292e] p-4 space-y-2' },
        props.widths.map((w) =>
            h('div', {
                class: 'h-3 animate-pulse rounded bg-white/10',
                style: { width: w },
            }),
        ),
    );

const htmlDivSkeleton = ['78%'];
const htmlScriptSkeleton = ['38%', '64%', '20%', '32%', '24%', '52%', '34%', '60%', '70%', '32%', '40%', '50%', '20%', '30%', '24%', '38%', '14%'];
const installSkeleton = ['46%', '0%', '14%', '0%', '46%'];
const pkgScriptSkeleton = ['58%', '20%', '34%', '36%', '52%', '64%', '60%', '34%', '34%', '14%', '20%', '60%'];

const props = defineProps({
    open: {
        type: Boolean,
        required: true,
    },
    apiToken: {
        type: Object as PropType<IToken>,
        required: true,
    },
});

const emit = defineEmits(['close', 'update:open']);

const tab = ref('html');

// --- Shared ---
const htmlDivCode = `<div id="app" style="position: absolute; width: 100%; height: 100%"></div>`;

// --- HTML tab code ---
// `<\/script>` escapes the closing tag so the surrounding Vue SFC <script> block
// isn't terminated when the file is parsed. eslint flags it as a useless escape
// here (it's inside a template literal) but removing the backslash breaks
// compilation in this SFC, so suppress it for the two offending lines.
const htmlScriptCode = computed(() => {
    return `<!--Import Editor Loader-->
${'<script src="https://v3.email-assets.topol.io/loader/build.js" type="text/javascript"></' + 'script>'}

<!--Define Plugin Options-->
<script>
const TOPOL_OPTIONS = {
  id: "#app",
  authorize: {
    apiKey: "${props.apiToken.token}",
    userId: "User-ID",
  },
  callbacks: {
    onSave: function(json, html) {
      // Handle save
    },
  },
};
${'</' + 'script>'}`;
});

// --- JS/TS Package tab code ---
const installCode = `npm install @topol.io/editor

//or

yarn add @topol.io/editor`;

const packageScriptCode = computed(() => {
    return `import TopolPlugin from "@topol.io/editor";

const TOPOL_OPTIONS = {
  id: "#app",
  authorize: {
    apiKey: "${props.apiToken.token}",
    userId: "some-user-id",
  },
  callbacks: {
    onSave(json, html) {},
  },
};

TopolPlugin.init(TOPOL_OPTIONS);`;
});

// --- Highlighted refs ---
const htmlDivHighlighted = ref('');
const htmlScriptHighlighted = ref('');
const installHighlighted = ref('');
const pkgScriptHighlighted = ref('');

const highlightCode = async () => {
    htmlDivHighlighted.value = await codeToHtml(htmlDivCode, {
        lang: 'html',
        theme: 'github-dark',
    });
    htmlScriptHighlighted.value = await codeToHtml(htmlScriptCode.value, {
        lang: 'html',
        theme: 'github-dark',
    });
    installHighlighted.value = await codeToHtml(installCode, {
        lang: 'bash',
        theme: 'github-dark',
    });
    pkgScriptHighlighted.value = await codeToHtml(packageScriptCode.value, {
        lang: 'typescript',
        theme: 'github-dark',
    });
};

watch(() => props.open, (newValue) => {
    if (newValue) {
        highlightCode();
    }
}, { immediate: true });

// --- Copy helpers ---
const { copy: copyToClipboard } = useClipboard();

// Top-level refs get auto-unwrapped to plain values in template expressions, so
// passing them straight into `copy(..., htmlDivCopied)` would hand the function
// a boolean instead of a Ref<boolean>. Use a single reactive map keyed by id
// to sidestep that whole class of bug.
type CopyKey = 'htmlDiv' | 'htmlScript' | 'install' | 'pkgHtml' | 'pkgScript';

const copied = reactive<Record<CopyKey, boolean>>({
    htmlDiv: false,
    htmlScript: false,
    install: false,
    pkgHtml: false,
    pkgScript: false,
});

const copy = async (text: string, key: CopyKey) => {
    await copyToClipboard(text);
    copied[key] = true;
    setTimeout(() => {
        copied[key] = false;
    }, 2000);
};

const handleClose = () => {
    emit('close');
    emit('update:open', false);
};
</script>
